import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { max, minBy, maxBy, orderBy, range } from 'lodash'
import { getYear } from '../../../utils/DateUtil'
import Select from '../../../components/forms/Select'
import i18n from 'simple-react-i18n'
import { getQualificationSelectOptions, getStatusSelectOptions } from '../../../utils/StatusUtil'
import { i18nize } from '../../../utils/StringUtil'
import { getValidPiezometrySeriesParams } from '../../../utils/PiezometryUtils'
import PiezometerSuiviTable from './PiezometerSuiviTable'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { componentHasHabilitations } from '../../../utils/HabilitationUtil'
import { H_STATION_PIEZO_FOLLOW_UP } from '../../../account/constants/AccessRulesConstants'
import { hasValue } from '../../../utils/NumberUtil'
import { getStationTitle, getStationTitleWithTownName } from '../../../utils/StationUtils'
import PiezometerStationAction from '../../../station/actions/PiezometerStationAction'
import { push } from '@lagunovsky/redux-react-router'
import { MEASURE_COTE, MEASURE_COTES } from '../../constants/PiezometryConstants'
import useTitle from 'utils/customHook/useTitle'
import {
    Alert, Card,
    CardContent,
    Grid,
} from '@mui/material'
import ProgressCard from 'components/card/ProgressCard'
import useProgressDispatch from 'utils/customHook/useProgressDispatch'
import { ButtonMUI } from 'components/styled/Buttons'
import RadioButtons from '../../../components/forms/RadioButtons'
import DtoPiezometer from '../../dto/DtoPiezometer'
import DtoPiezometerAdditionalMeasures from '../../../station/dto/piezometer/DtoPiezometerAdditionalMeasures'
import DtoMeasureStats from '../../../station/dto/piezometer/DtoMeasureStats'
import {
    AUTO,
    CHRONIC_TYPE,
    DATE,
    DISPLAY_COTE, HISTORIC, HORIZON,
    HOUR,
    MAX,
    QUALIFICATION,
    YEAR,
} from '../../../utils/constants/MeasureConstants'
import { STATION_TYPE_NAME } from '../../../station/constants/StationConstants'
import MessageCard from '../../../components/card/MessageCard'
import { getGeneralLevelling } from '../../../utils/SettingUtils'

const DEFAULT_CHRONIC_TYPE = 'measures'
const CHRONIC_DATA_TYPE = -1

const YearsSelectComponent = ({
    tmpFilter,
    measures,
    onChangeFilter = () => {},
}) => {
    const minMeasure = minBy(measures, 'date')
    const minYear = getYear(minMeasure?.date)
    const years = range(moment().year(), parseInt(minYear) -1).map(y => ({ code: y, name: y }))

    return (tmpFilter.horizon === YEAR && !minMeasure) ? (
        <Grid size={12}>
            <Select
                label={i18n.year}
                disabled
            />
            <Alert severity='warning' sx={{ fontSize: '13px', '& .MuiAlert-message': { padding: '10px 0 8px' } }}>
                {i18n.nothingToDoOrProcess}
            </Alert>
        </Grid>
    ) : (tmpFilter.horizon === YEAR) ? (
        <Grid size={12}>
            <Select
                options={years}
                noSort
                onChange={e => onChangeFilter(YEAR, e)}
                label={i18n.year}
                value={tmpFilter.year}
                data-cy={'select-year'}
            />
        </Grid>
    ) : (
        <Grid size={12}>
            <Select
                label={i18n.year}
                disabled
            />
        </Grid>
    )
}

YearsSelectComponent.propTypes = {
    tmpFilter: PropTypes.shape({
        horizon: PropTypes.string,
        state: PropTypes.number,
        qualification: PropTypes.number,
        year: PropTypes.number,
        chronicType: PropTypes.string,
        displayCote: PropTypes.number,
    }),
    measures: PropTypes.arrayOf(PropTypes.shape({})),
    onChangeFilter: PropTypes.func,
}

const PiezometerSuiviTableApp = ({}) => {
    const {
        piezometer,
        piezometerAdditionalMeasures,
        piezometerStatistics,
        citiesIndex,
    } = useSelector(store => ({
        piezometer: store.StationReducer.piezometer,
        piezometerAdditionalMeasures: store.PiezometerStationReducer.piezometerAdditionalMeasures,
        piezometerStatistics: store.PiezometerStationReducer.piezometerStatistics,
        citiesIndex: store.CityReducer.citiesIndex,
    }), shallowEqual)

    const defaultFilter = {
        horizon: YEAR,
        state: null,
        qualification: null,
        year: moment().year(),
        chronicType: DEFAULT_CHRONIC_TYPE,
        displayCote: MEASURE_COTE.NGF,
    }
    const [filter, setFilter] = useState(defaultFilter)
    const [tmpFilter, setTmpFilter] = useState(defaultFilter)
    const [measures, setMeasures] = useState([])

    const dispatch = useDispatch()

    const MEASURE_COTES = [
        {
            key: 1,
            label: getGeneralLevelling(),
        },
        {
            key: 2,
            label: i18n.depthLastlandmark,
        },
        {
            key: 3,
            label: i18n.ground,
        },
        {
            key: 4,
            label: i18n.landmarkHistory,
        },
    ]


    const minMeasure = minBy(measures, 'date')
    const minYear = getYear(minMeasure?.date)
    const maxMeasure = maxBy(measures, 'date')
    const maxYear = getYear(maxMeasure?.date)

    const {
        isLoaded: dataIsLoaded,
        progress: dataProgress,
    } = useProgressDispatch(() => hasValue(piezometer.id) ? [
        dispatch(PiezometerStationAction.getPiezoChartMeasures({
            dataType: CHRONIC_DATA_TYPE,
            displayCote: filter.displayCote,
            groupFunc: MAX,
            stationId: piezometer.id,
        })).then(result => setMeasures(result.measures.filter(m => !!m.value))),
        dispatch(PiezometerStationAction.loadPiezometerAdditionalMeasures(piezometer.id)),
    ] : [], [filter.chronicType, filter.displayCote])

    useEffect(() => {
        if (!componentHasHabilitations(H_STATION_PIEZO_FOLLOW_UP)) {
            dispatch(push('/unauthorized'))
        }

        if (hasValue(piezometer.id)) {
            dispatch(PiezometerStationAction.fetchPiezometerSamples(piezometer.id))
            dispatch(PiezometerStationAction.fetchPiezoMeasuresStats(piezometer.id))
        }
    }, [])

    const townName = useMemo(() => citiesIndex[piezometer.townCode]?.name, [citiesIndex, piezometer])

    useTitle(() => [{
        title: i18n.piezometry,
        href: STATION_TYPE_NAME.piezometry,
    }, {
        title: getStationTitleWithTownName(piezometer, townName),
        href: `station/piezometry/${piezometer.id}`,
    }, {
        title: i18n.followUpTable,
        href: `station/piezometry/${piezometer.id}/piezoSuiviTable`,
    }], [piezometer, townName])

    const onChangeFilter = (filterName, value) => setTmpFilter(prev => ({ ...prev, [filterName]: value }))

    const chronicTypeOptions = useMemo(() => {
        const additionnalSeries = piezometerAdditionalMeasures.reduce((acc, typeObj) => {
            const dataType = piezometerStatistics.find(t => t.typeId === typeObj.type)
            if (dataType) {
                return [
                    ...acc,
                    {
                        code: `additional${typeObj.type}`,
                        label: dataType.label,
                        dateKey: DATE,
                        hourKey: HOUR,
                        groupFunc: max,
                        type: typeObj.type,
                        order: dataType.order,
                    },
                ]
            }
            return acc
        }, [])

        return orderBy(i18nize(getValidPiezometrySeriesParams()).concat(additionnalSeries), 'order')
    }, [piezometerAdditionalMeasures, piezometerStatistics])

    return (
        <Grid container padding={1} spacing={1}>
            <Grid container size={12} direction={'row'}>
                <Card sx={{ width: '100%' }}>
                    <CardContent>
                        <Grid container alignItems={'center'} spacing={1}>
                            <Grid size={2}>
                                <Select
                                    options={chronicTypeOptions}
                                    noSort
                                    label={i18n.chronicType}
                                    onChange={e => onChangeFilter(CHRONIC_TYPE, e)}
                                    value={tmpFilter.chronicType}
                                />
                            </Grid>
                            <Grid size={2}>
                                <Select
                                    options={MEASURE_COTES}
                                    noSort
                                    keyValue='key'
                                    keyLabel='label'
                                    label={i18n.displayMode}
                                    onChange={e => onChangeFilter(DISPLAY_COTE, e)}
                                    value={tmpFilter.displayCote}
                                />
                            </Grid>
                            <Grid size={2}>
                                <Select
                                    options={getStatusSelectOptions()}
                                    label={i18n.status}
                                    nullLabel='&nbsp;'
                                    onChange={e => onChangeFilter('state', e)}
                                    value={tmpFilter.state}
                                />
                            </Grid>
                            <Grid size={2}>
                                <Select
                                    options={getQualificationSelectOptions()}
                                    label={i18n.qualification}
                                    nullLabel='&nbsp;'
                                    onChange={e => onChangeFilter(QUALIFICATION, e)}
                                    value={tmpFilter.qualification}
                                />
                            </Grid>
                            <Grid size={1.5}>
                                <RadioButtons
                                    colOption={ 12 }
                                    selected={tmpFilter.horizon}
                                    title={i18n.horizon}
                                    onChange={(e) => onChangeFilter(HORIZON, e)}
                                    elements={[
                                        { code: YEAR, name: i18n.perYear },
                                        { code: HISTORIC, name: i18n.historic },
                                    ]}
                                />
                            </Grid>
                            {
                                tmpFilter.horizon === YEAR && (
                                    <Grid size={1}>
                                        <YearsSelectComponent
                                            tmpFilter={tmpFilter}
                                            measures={measures}
                                            onChangeFilter={onChangeFilter}
                                        />
                                    </Grid>
                                )
                            }
                            <ButtonMUI
                                onClick={() => setFilter(tmpFilter)}
                                sx={{ marginLeft: AUTO }}
                                variant='contained'
                                data-cy={'search'}
                            >
                                {i18n.search}
                            </ButtonMUI>
                        </Grid>
                    </CardContent>
                </Card>
                {
                    !dataIsLoaded ? (
                        <Grid size={12}>
                            <ProgressCard progress={dataProgress} withMessage />
                        </Grid>
                    ): (
                        <Grid container size={12}>
                            { !measures.length ? (
                                <MessageCard>{i18n.noDataToDisplay}</MessageCard>
                            ) : (
                                <Grid container size={12} sx={{ height: '70vh' }}>
                                    <PiezometerSuiviTable
                                        piezometer={piezometer}
                                        filter={filter}
                                        historic={filter.horizon === HISTORIC}
                                        measures={measures}
                                        chronicTypes={chronicTypeOptions}
                                        tableHeight='100%'
                                        maxYear={maxYear}
                                        minYear={minYear}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    )
                }
            </Grid>
        </Grid>
    )
}

PiezometerSuiviTableApp.propTypes = {
    piezometer: PropTypes.instanceOf(DtoPiezometer),
    piezometerAdditionalMeasures: PropTypes.arrayOf(PropTypes.instanceOf(DtoPiezometerAdditionalMeasures)),
    piezometerStatistics: PropTypes.arrayOf(PropTypes.instanceOf(DtoMeasureStats)),
}


export default PiezometerSuiviTableApp
